import React, { useRef, useCallback } from 'react'
import classNames from 'classnames'
// eslint-disable-next-line import/no-named-default
import { default as MultiCarousel } from 'react-multi-carousel'
import VisuallyHidden from '@reach/visually-hidden'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import kebabCase from 'lodash-es/kebabCase'
import unescape from 'lodash-es/unescape'

import useHasMounted from 'hooks/useHasMounted'
import { OnDemandCard } from '../OnDemand/OnDemandCard'

import 'react-multi-carousel/lib/styles.css'

import styles from './styles.module.scss'
import { IS_BROWSER } from '../../../localConstants'

export const Carousel = ({
  name,
  description,
  index,
  items,
  className,
  initialIndex,
  isSameGroup,
}) => {
  const roundToMin = (duration) => Math.ceil(duration / 60)
  const upscaleThumbnailImg = (url) => `${url.slice(0, -7)}330x198`

  const carouselBreakpoints = {
    xxxl: {
      breakpoint: { max: 8000, min: 2201 },
      items: 7,
      slidesToSlide: 7,
      partialVisibilityGutter: 20,
    },
    xxl: {
      breakpoint: { max: 2200, min: 1846 },
      items: 6,
      slidesToSlide: 6,
      partialVisibilityGutter: 20,
    },
    xl: {
      breakpoint: { max: 1845, min: 1481 },
      items: 5,
      slidesToSlide: 5,
      partialVisibilityGutter: 20,
    },
    lg: {
      breakpoint: { max: 1480, min: 1131 },
      items: 4,
      slidesToSlide: 4,
      partialVisibilityGutter: 20,
    },
    md: {
      breakpoint: { max: 1130, min: 901 },
      items: 3,
      slidesToSlide: 3,
      partialVisibilityGutter: 20,
    },
    sm: {
      breakpoint: { max: 900, min: 466 },
      items: 2,
      slidesToSlide: 2,
      partialVisibilityGutter: 20,
    },
    xs: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      partialVisibilityGutter: 20,
    },
  }

  const carouselContainerRef = useRef()
  const handleArrowClick = (onClick) => {
    carouselContainerRef.current.style.setProperty('--arrowDelay', '300ms')
    onClick()
  }

  const CustomRightArrow = ({ onClick, style }) => {
    const buttonRef = useRef()

    return (
      <button
        ref={buttonRef}
        onClick={() => handleArrowClick(onClick)}
        className={classNames(styles.carouselArrow, styles.carouselRightArrow)}
        type="button"
        style={style}
        data-testid="right arrow"
      >
        <VisuallyHidden>Go to more videos</VisuallyHidden>
        <FontAwesomeIcon icon="chevron-right" />
      </button>
    )
  }

  const CustomLeftArrow = ({ onClick, style }) => {
    const buttonRef = useRef()
    return (
      <button
        ref={buttonRef}
        onClick={() => handleArrowClick(onClick)}
        className={classNames(styles.carouselArrow, styles.carouselLeftArrow)}
        type="button"
        style={style}
        data-testid="left arrow"
      >
        <VisuallyHidden>Go to previous videos</VisuallyHidden>
        <FontAwesomeIcon icon="chevron-left" />
      </button>
    )
  }

  // There's no initial property on the carousel,
  // so we need to use the goToSlide method immediately when
  // it mounts and we hide the shift by delaying it's visibility
  const carouselRef = useCallback(
    (node) => {
      if (initialIndex && node !== null) {
        node.goToSlide(initialIndex)
      }
    },
    [initialIndex]
  )
  const hasMounted = useHasMounted()

  return (
    IS_BROWSER &&
    hasMounted && (
      <div
        key={name}
        className={classNames(styles.carouselSection, className, {
          [styles.disableAnimation]: isSameGroup,
        })}
        ref={carouselContainerRef}
        style={{
          '--arrowDelay': `${(index + 1) * 550}ms`,
        }}
      >
        <div
          className={styles.groupLabel}
          style={{
            '--groupLabelDelay': `${(index + 1) * 125}ms`,
          }}
        >
          <h2 data-testid="groupLabel" className={styles.carouselTitle}>
            {unescape(name)}
          </h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <MultiCarousel
          ref={carouselRef}
          removeArrowOnDeviceType={['xs', 'sm']}
          customRightArrow={<CustomRightArrow />}
          customLeftArrow={<CustomLeftArrow />}
          className={styles.carousel}
          centerMode
          focusOnSelect={initialIndex !== undefined}
          responsive={carouselBreakpoints}
        >
          {Object.values(items).map((videoObj, videoIndex) => (
            <OnDemandCard
              className={styles.cardContainer}
              index={videoIndex}
              categoryIndex={index}
              title={videoObj.name}
              img={upscaleThumbnailImg(videoObj.thumbnail.url)}
              length={roundToMin(videoObj.duration)}
              link={`/on-demand/${kebabCase(unescape(name))}/${kebabCase(
                videoObj.name
              )}`}
              key={videoObj.id}
              style={{
                '--cardDelay': 200 + (175 * videoIndex + index * 220),
              }}
            />
          ))}
        </MultiCarousel>
      </div>
    )
  )
}
